/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/anchor-is-valid */
import './ContentForm.scss';
import { Content } from 'interface/content/content.interface';
import { ContentListItem, CONTENT_MANAGER_STEPS } from 'components/ExperienceCanvas/types';
import { FlightButton, FlightSelect } from '@flybits/design-system';
import { usePortalContainer } from 'components/Shared/LargeModal/PortalContainerContext';
import { SelectOption } from 'types/common';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useAppSelector as useSelector, useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { TExperienceCanvasRouteParams } from 'pages/ExperienceCanvas/types';
import { ZoneConfig } from 'pages/Zones/types';
import ContentAPI from 'services/api/content.api';
import ContentList from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/ContentEditorBox/ContentList/ContentList';
import ContentManager from 'components/ExperienceCanvas/ContentManager/ContentManager';
import UsedByModal from 'components/Modal/UsedByModal/UsedByModal';
import InAppContentIcon from 'pages/ExperienceCanvas/assets/icons/InAppContentIcon';
import React, { useState, useEffect, useMemo } from 'react';
import useContentInstances from 'hooks/useContentInstances';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useSettings from 'hooks/useSetting';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import { extractContentPreviewFields } from 'helpers/content.helper';
import { getContentListItems } from 'store/content/content.selector';
import ZoneAPI from 'services/api/zone.api';
import { ContentFormProps } from '../pushDraft.types';

const CLASS_PREFIX = 'content-form';
const CLASSES = {
  MAIN: CLASS_PREFIX,
  INSTRUCTIONS: `${CLASS_PREFIX}__instructions`,
  TIP: `${CLASS_PREFIX}__tip`,
  BUTTON_LABEL: `${CLASS_PREFIX}__button-label`,
  SELECT_PLACEHOLDER: `${CLASS_PREFIX}__select-placeholder`,
  SELECT_REQUIRED_SYMBOL: `${CLASS_PREFIX}__required-symbol`,
};

export default function ContentForm({
  contentDraft,
  stepIdx,
  isJourneyLocked,
  contentManagerSingleSelectOnly = true,
  stepPushRefId,
  onSelectContent,
  onRemoveContent,
}: ContentFormProps) {
  const contentAPI = useMemo(() => new ContentAPI(), []);
  const zoneAPI = useMemo(() => new ZoneAPI(), []);
  const dispatch = useDispatch();
  const { flags } = useFeatureFlag();
  const history = useHistory();
  const { pid: projectId } = useRouteMatch<TExperienceCanvasRouteParams>().params;
  const { languages } = useSettings();
  const defaultLang = getDefaultLanguage(languages);
  const portalContainer = usePortalContainer();

  const [isContentManagerVisible, setIsContentManagerVisible] = useState(false);
  const [isUsedByModalVisible, setIsUsedByModalVisible] = useState(false);
  const [specificContent, setSpecificContent] = useState<ContentListItem | null>(null);
  const [contentManagerInitialStep, setContentManagerInitialStep] = useState<CONTENT_MANAGER_STEPS | undefined>();

  const contentListItemsFromStore = useSelector((state) => getContentListItems(state, stepIdx));

  const displayableContentList = useMemo((): ContentListItem[] => {
    if (contentDraft.cleared) {
      return [];
    }

    if (contentDraft.selectedContent) {
      const content = contentDraft.selectedContent;
      const previewFields = extractContentPreviewFields(content, defaultLang);
      const storeMatch = contentListItemsFromStore.find((item) => item.id === content.id);

      return [
        {
          refId: storeMatch?.refId ?? contentDraft.selectedContentRefId ?? '',
          stepIdx,
          name: content.localizations?.[defaultLang]?.name ?? storeMatch?.name ?? '',
          metadataDescription:
            content.localizations?.[defaultLang]?.description ?? storeMatch?.metadataDescription ?? '',
          type: 'primary' as const,
          isRequired: true,
          templateIdConstraint: storeMatch?.templateIdConstraint ?? '',
          isReadOnly: storeMatch?.isReadOnly ?? false,
          prototypeId: storeMatch?.prototypeId ?? '',
          ...previewFields,
        },
      ];
    }

    if (contentDraft.selectedContentRefId) {
      const storeItem = contentListItemsFromStore.find(
        (item) => item.refId === contentDraft.selectedContentRefId,
      );
      if (storeItem) {
        return [storeItem];
      }
    }

    return [];
  }, [contentDraft, stepIdx, defaultLang, contentListItemsFromStore]);

  const handleAddContent = () => {
    setIsContentManagerVisible(true);
  };

  const handleDelete = () => {
    onRemoveContent();
  };

  const handleDisconnect = () => {
    onRemoveContent();
  };

  const ButtonLabel = ({ children }: { children: string }) => (
    <div className={CLASSES.BUTTON_LABEL}>
      <InAppContentIcon />
      {children}
    </div>
  );

  const openInEditMode = () => {
    setContentManagerInitialStep(CONTENT_MANAGER_STEPS.CONTENT_EDITOR);
    setIsContentManagerVisible(true);
    setIsUsedByModalVisible(false);
  };

  const handleEdit = async (content: ContentListItem) => {
    setSpecificContent(content);
    if (content.usedBy && content.usedBy.length > 0) {
      setIsUsedByModalVisible(true);
    } else {
      openInEditMode();
    }
  };

  const handleOpenTargetedSelection = (content: ContentListItem) => {
    setSpecificContent(content);
    setIsContentManagerVisible(true);
  };

  const handleMakePrimary = () => {
    // No-op in single-select flow push mode
  };

  const showAddMoreBtn = false;

  const [moduleDropdownOptions, setModuleDropdownOptions] = useState<SelectOption[]>([]);
  const [moduleLabelDropdownOptions, setModuleLabelDropdownOptions] = useState<SelectOption[]>([]);
  const [zoneDropdownOptions, setZoneDropdownOptions] = useState<SelectOption[]>([]);
  const [isZMButtonDisabled, setIsZMButtonDisabled] = useState(false);
  const [selectedModuleLabel, setSelectedModuleLabel] = useState<SelectOption | undefined>();
  const [selectedModule, setSelectedModule] = useState<SelectOption | undefined>();
  const [selectedZone, setSelectedZone] = useState<SelectOption | undefined>();
  const [zmErrorMessage, setZMErrorMessage] = useState<string>('');
  const [zoneConfig, setZoneConfig] = useState<ZoneConfig>();
  const { contentInstances } = useContentInstances();

  const handleZoneSelection = (option: SelectOption) => {
    setSelectedZone(option);
    setSelectedModule(undefined);
    setSelectedModuleLabel(undefined);

    const selectedZoneModules = zoneConfig?.zones.filter((zone) => {
      return zone.id === option.key;
    });

    if (selectedZoneModules?.length) {
      const options: SelectOption[] = [];

      selectedZoneModules[0].modules.forEach((module) => {
        options.push({
          key: module.id,
          name: module.name,
        });
      });

      setModuleDropdownOptions(options);
    }
  };

  const handleModuleSelection = (option: SelectOption) => {
    setSelectedModule(option);
    setSelectedModuleLabel(undefined);

    const zones = zoneConfig?.zones ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let module: any = null;

    for (let i = 0; i < zones.length; i++) {
      const modules = zones[i]?.modules ?? [];

      if (modules) {
        for (let j = 0; j < modules.length; j++) {
          if (modules[j].id === option.key) {
            module = modules[j];
            break;
          }
        }
      }
    }

    const options: SelectOption[] = [];

    module?.labelsOrder?.forEach((label: string, index: number) => {
      options.push({
        key: index.toString(),
        name: label,
      });
    });

    setModuleLabelDropdownOptions(options);
  };

  const handleModuleLabelSelection = (option: SelectOption) => {
    setSelectedModuleLabel(option);
  };

  const applyZMLabel = async () => {
    const label = selectedModuleLabel?.name;
    const updatedContentPromises: Promise<Content>[] = [];
    const contentList = displayableContentList.filter((c) => c.id != '');

    if (!label) {
      setZMErrorMessage('Select a label to apply');
      return;
    }

    if (contentInstances.length <= 0) {
      setZMErrorMessage('Failed to retrieve all content');
      return;
    } else if (contentList.length <= 0) {
      setZMErrorMessage('Add content before applying the label');
      return;
    } else {
      setIsZMButtonDisabled(true);
      setZMErrorMessage('');

      contentList.forEach((content) => {
        const filteredContents = contentInstances.filter((c: { id: string }) => c.id === content.id);

        if (filteredContents.length > 0 && !filteredContents[0].labels.includes(label)) {
          const updatedInstance = {
            ...filteredContents[0],
            labels: [...filteredContents[0].labels, label],
          };
          updatedContentPromises.push(contentAPI.updateInstance(updatedInstance, content.id));
        }
      });

      if (updatedContentPromises.length <= 0) {
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: {
            title: 'No changes made',
            content: `The label ${label} already exists for all selected content.`,
            type: 'info',
          },
        });
        setIsZMButtonDisabled(false);
        return;
      }
      try {
        await Promise.all(updatedContentPromises);
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: {
            title: 'Success!',
            content: `The label ${label} was added to all selected content.`,
            type: 'success',
          },
        });
        setIsZMButtonDisabled(false);
      } catch (error) {
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: {
            title: 'Error',
            content: `The label ${label} could not be added to all selected content. ${error}`,
            type: 'error',
          },
        });
        setIsZMButtonDisabled(false);
      }
    }
  };

  useEffect(() => {
    const options: SelectOption[] = [];

    const getZoneConfig = async () => {
      try {
        const config = await zoneAPI.getZoneConfig();
        setZoneConfig(config);
        config.zones?.forEach((zone) => {
          if (zone.id) {
            options.push({
              key: zone.id,
              name: zone.name,
            });
          }
        });

        setZoneDropdownOptions(options);
      } catch (exception) {
        //TODO: Handle exception
      }
    };

    getZoneConfig();
  }, [zoneAPI]);

  const redirectToZM = () => {
    history.push(`/project/${projectId}/zones`);
  };

  const handleConfirmContentSelection = (contents: Content[]) => {
    const selected = contents[0];
    if (selected) {
      onSelectContent(selected, specificContent?.refId ?? contentDraft.selectedContentRefId);
    }
    setIsContentManagerVisible(false);
    setSpecificContent(null);
    setContentManagerInitialStep(undefined);
  };

  const handleContentEdited = (content: Content) => {
    onSelectContent(content, contentDraft.selectedContentRefId ?? specificContent?.refId);
    setIsContentManagerVisible(false);
    setSpecificContent(null);
    setContentManagerInitialStep(undefined);
  };

  return (
    <>
      <div className={CLASSES.MAIN}>
        {displayableContentList.length < 1 && (
          <FlightButton
            theme="secondary"
            label={<ButtonLabel>Add Content +</ButtonLabel>}
            size="large"
            disabled={isJourneyLocked}
            onClick={handleAddContent}
          />
        )}
        <p className={CLASSES.INSTRUCTIONS}>Content added here will appear in the user&apos;s feed.</p>
        <ContentList
          contents={displayableContentList}
          onEditClick={handleEdit}
          onDeleteClick={handleDelete}
          onMakePrimaryClick={handleMakePrimary}
          onSelectContentClick={handleOpenTargetedSelection}
          onDisconnectClick={handleDisconnect}
          currentStepIdx={stepIdx}
          contentManagerSingleSelectOnly={contentManagerSingleSelectOnly}
        />
        {showAddMoreBtn && (
          <FlightButton
            theme="secondary"
            label={<ButtonLabel>Add More +</ButtonLabel>}
            size="large"
            onClick={handleAddContent}
          />
        )}
        {!contentManagerSingleSelectOnly && (
          <p className={CLASSES.TIP}>
            <strong>Recommended:</strong> 6 max.
          </p>
        )}
        {flags['tx_zone_module_label_to_content'] && (
          <div className="zone-module-selection">
            <div className={displayableContentList.length < 1 ? 'is-no-content' : ''}>
              <h3>Zones & Modules</h3>
              <p>
                This is an <strong>optional</strong> configuration to place above content(s) to a specific zone.
              </p>

              {zoneDropdownOptions.length < 1 && (
                <div className="no-zone-message flight-snackbar flight-snackbar--info">
                  <div className="flight-snackbar__content">
                    This project does not have any zones. Navigate to &nbsp;
                    <a href="#" onClick={redirectToZM}>
                      Zones & Modules
                    </a>
                    &nbsp; page to define a zone.
                  </div>
                </div>
              )}

              <div className={zoneDropdownOptions.length < 1 ? 'is-no-zone' : ''}>
                <label className="flight-select-label">Select a zone</label>
                <FlightSelect
                  options={zoneDropdownOptions}
                  selected={
                    (selectedZone as SelectOption | undefined) || {
                      key: '',
                      name: 'No zone selected',
                    }
                  }
                  disabled={isZMButtonDisabled || zoneDropdownOptions.length < 1}
                  handleOptionClick={handleZoneSelection}
                  width="100%"
                  portalContainer={portalContainer}
                />

                <div className="module-dropdown">
                  <label className="flight-select-label">Select a module</label>
                  <FlightSelect
                    options={moduleDropdownOptions}
                    selected={
                      (selectedModule as SelectOption | undefined) || {
                        key: '',
                        name: 'No module selected',
                      }
                    }
                    disabled={isZMButtonDisabled || !selectedZone}
                    handleOptionClick={handleModuleSelection}
                    width="100%"
                    portalContainer={portalContainer}
                  />
                </div>

                <div className="module-label-dropdown">
                  <label className="flight-select-label">Select a label</label>
                  <FlightSelect
                    options={moduleLabelDropdownOptions}
                    selected={
                      (selectedModuleLabel as SelectOption | undefined) || {
                        key: '',
                        name: 'No label selected',
                      }
                    }
                    disabled={isZMButtonDisabled || !selectedModule}
                    handleOptionClick={handleModuleLabelSelection}
                    width="100%"
                    portalContainer={portalContainer}
                  />{' '}
                </div>
              </div>
            </div>
            <div className="button-container">
              <FlightButton
                className={'apply-zone-module-label-button'}
                theme="primary"
                label={'Apply Label'}
                loading={isZMButtonDisabled}
                disabled={!selectedModuleLabel || isZMButtonDisabled || displayableContentList.length < 1}
                onClick={applyZMLabel}
                onKeyDown={applyZMLabel}
              />
              {zmErrorMessage && <span className="error-message">{zmErrorMessage}</span>}
            </div>
          </div>
        )}
      </div>
      <ContentManager
        isOpen={isContentManagerVisible}
        stepIdx={stepIdx}
        contentRefId={specificContent?.refId}
        editContentInstanceId={specificContent?.id}
        editContentPrototypeId={specificContent?.prototypeId}
        startingStep={contentManagerInitialStep}
        contentManagerSingleSelectOnly={contentManagerSingleSelectOnly}
        isFromPushNodeFromFlowBuilder={!!stepPushRefId}
        bypassRedux
        onConfirmContentSelection={handleConfirmContentSelection}
        onContentEdited={handleContentEdited}
        onClickClose={() => {
          setIsContentManagerVisible(false);
          setSpecificContent(null);
          setContentManagerInitialStep(undefined);
        }}
      />
      <UsedByModal
        isOpen={isUsedByModalVisible}
        closeModal={() => setIsUsedByModalVisible(false)}
        referencedBy={specificContent?.usedBy ?? []}
        primaryAction={{
          label: 'Continue',
          onClickHandler: () => {
            specificContent && openInEditMode();
          },
        }}
        secondaryAction={{ label: 'Cancel', onClickHandler: () => setIsUsedByModalVisible(false) }}
      />
    </>
  );
}
